import { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Lightweight toast system.
 *
 * Two ways to fire toasts:
 *   1. Inside a React component: `const toast = useToast(); toast.success("...")`.
 *   2. From anywhere (e.g. from QueryClient's MutationCache): `toastBus.emit("success", "...")`.
 *
 * The bus is a tiny pub/sub that the ToastProvider subscribes to on
 * mount, so non-React code (like global mutation hooks) can show
 * toasts without an extra prop drilled hook.
 */
type Kind = "success" | "error" | "info";
interface Item { id: number; kind: Kind; text: string; }
interface Api {
  success: (text: string) => void;
  error:   (text: string) => void;
  info:    (text: string) => void;
}

type Listener = (kind: Kind, text: string) => void;
const listeners = new Set<Listener>();
export const toastBus = {
  emit(kind: Kind, text: string) {
    listeners.forEach((l) => l(kind, text));
  },
};

const Ctx = createContext<Api | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const push = useCallback((kind: Kind, text: string) => {
    setItems((prev) => [...prev, { id: Date.now() + Math.random(), kind, text }]);
  }, []);

  useEffect(() => {
    listeners.add(push);
    return () => { listeners.delete(push); };
  }, [push]);

  const api: Api = {
    success: (t) => push("success", t),
    error:   (t) => push("error", t),
    info:    (t) => push("info", t),
  };
  return (
    <Ctx.Provider value={api}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 pointer-events-none"
      >
        {items.map((it) => (
          <ToastItem key={it.id} item={it} onDismiss={() => setItems((p) => p.filter((x) => x.id !== it.id))} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

function ToastItem({ item, onDismiss }: { item: Item; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);
  const bg = item.kind === "success" ? "var(--color-success)"
           : item.kind === "error"   ? "var(--color-error)"
           : "var(--color-primary)";
  return (
    <button
      type="button"
      onClick={onDismiss}
      className="pointer-events-auto text-white px-4 py-2 rounded-md shadow-card text-sm flex items-center gap-2 max-w-xs"
      style={{ background: bg }}
    >
      <span aria-hidden>{item.kind === "success" ? "OK" : item.kind === "error" ? "!" : "i"}</span>
      <span className="text-left">{item.text}</span>
    </button>
  );
}

export function useToast(): Api {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return {
      success: (t) => toastBus.emit("success", t),
      error:   (t) => toastBus.emit("error", t),
      info:    (t) => toastBus.emit("info", t),
    };
  }
  return ctx;
}
